/**
 * EquipmentChecks.js — блок «Осмотры» на карточке оборудования
 * (equipment_checks_plan.md).
 *
 * Кнопки — шаблоны чек-листов организации, подходящие типу единицы (сервер
 * отфильтровал по применимости разделов). Нажатие создаёт Работу-инспекцию,
 * привязанную к единице, и открывает её в InspectionRunner. Ниже — история
 * осмотров с итогом. Нет шаблонов и нет истории — блока нет вовсе: у тенанта
 * без «бумажных» шагов карточка не обрастает пустыми секциями.
 *
 * Право запуска — equipment.run_checks (приходит с карточкой как can_run_checks).
 * PostHog: equipment_check_started / equipment_check_opened.
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { api } from '../api/client';
import { track } from '../analytics';

const STAGE_ORDER = ['acceptance', 'service', 'visit', 'release'];

function fmtDate(s, lang) {
  if (!s) return '';
  const d = new Date(String(s).includes('T') ? s : `${String(s).replace(' ', 'T')}Z`);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(lang || undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

function Chip({ color, children }) {
  return (
    <span style={{
      display: 'inline-block', padding: '1px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600,
      background: `${color}1f`, color,
    }}>{children}</span>
  );
}

export default function EquipmentChecks({ equipmentId, propertyId, canRun, reloadKey, onOpenTask, onClose }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.split('-')[0] || 'ru';
  const [templates, setTemplates] = useState([]);
  const [history, setHistory] = useState([]);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      // Два независимых запроса: сбой истории не должен прятать кнопки запуска, и наоборот.
      // Блок вспомогательный — ошибка не ломает карточку.
      const [tpl, hist] = await Promise.all([
        canRun ? api.get(`/equipment/${equipmentId}/check-templates`).catch(() => []) : Promise.resolve([]),
        api.get(`/equipment/${equipmentId}/checks`).catch(() => []),
      ]);
      if (!alive) return;
      setTemplates(Array.isArray(tpl) ? tpl : []);
      setHistory(Array.isArray(hist) ? hist : []);
    })();
    return () => { alive = false; };
  }, [equipmentId, canRun, reloadKey]);

  if (templates.length === 0 && history.length === 0) return null;

  const open = (taskId) => {
    if (!onOpenTask || !taskId) return;
    onOpenTask(taskId, propertyId);
    onClose && onClose();
  };

  const start = async (tpl) => {
    if (busyId) return;
    setBusyId(tpl.id);
    try {
      const r = await api.post(`/equipment/${equipmentId}/checks`, { template_id: tpl.id });
      track('equipment_check_started', { stage: tpl.stage || 'none', existing: !!r.existing });
      open(r.task_id);
    } catch (e) {
      toast.error(e.code === 'DRAFT' ? t('equipmentChecks.draft') : (e.message || t('equipmentChecks.startError')));
    } finally {
      setBusyId(null);
    }
  };

  const groups = STAGE_ORDER
    .map((st) => ({ stage: st, items: templates.filter((x) => (x.stage || null) === st) }))
    .filter((g) => g.items.length);

  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase',
                    letterSpacing: '.05em', marginBottom: 8 }}>
        {t('equipmentChecks.title')}
      </div>

      {groups.map((g) => (
        <div key={g.stage || 'none'} style={{ marginBottom: 8 }}>
          {groups.length > 1 && (
            <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 4 }}>
              {g.stage ? t(`checklists.stage.${g.stage}`) : t('equipmentChecks.otherStage')}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {g.items.map((tpl) => (
              <button key={tpl.id} type="button" className="btn btn-secondary btn-sm"
                      data-ph-capture="equipment_check_start" disabled={busyId === tpl.id}
                      onClick={() => start(tpl)}>
                {tpl.name}
              </button>
            ))}
          </div>
        </div>
      ))}

      {history.length > 0 && (
        <div style={{ marginTop: 8 }}>
          {history.map((h) => {
            const s = h.summary || {};
            return (
              <div key={h.id} className="card clickable" data-ph-capture="equipment_check_open"
                   style={{ padding: 10, marginBottom: 6, cursor: onOpenTask ? 'pointer' : 'default' }}
                   onClick={() => { track('equipment_check_opened', { stage: h.stage || 'none' }); open(h.id); }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'baseline' }}>
                  <span style={{ fontWeight: 600, fontSize: 14 }}>{h.template_name || h.title}</span>
                  <span style={{ fontSize: 12, color: 'var(--text3)' }}>{fmtDate(h.created_at, lang)}</span>
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginTop: 4 }}>
                  {s.not_ok > 0 && <Chip color="#c93b40">{t('equipmentChecks.defects', { count: s.not_ok })}</Chip>}
                  {s.pending > 0 && <Chip color="#6b7280">{t('equipmentChecks.pending', { count: s.pending })}</Chip>}
                  {s.not_ok === 0 && s.pending === 0 && <Chip color="#2a9d63">{t('equipmentChecks.passed')}</Chip>}
                  {h.author_name && <span style={{ fontSize: 12, color: 'var(--text3)' }}>{h.author_name}</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
